import { Injectable, computed, signal } from '@angular/core';
import type { MeResponse } from '@lance-x/shared';

/**
 * Global signal-based auth state. Read access is through computed signals so
 * components stay reactive; writes happen via setSession/clear from AuthService.
 *
 * Access token is intentionally kept here (not in TokenStore) because it's the
 * value templates need synchronously for rendering Authorization headers. The
 * TokenStore exists for refresh-token portability across PWA/Capacitor.
 */
@Injectable({ providedIn: 'root' })
export class AuthStore {
  readonly user = signal<MeResponse | null>(null);
  readonly accessToken = signal<string | null>(null);

  readonly isAuthenticated = computed(
    () => this.user() !== null && this.accessToken() !== null,
  );
  readonly role = computed(() => this.user()?.role ?? null);
  readonly canMutateEmpresa = computed(
    () => this.role() === 'EMPRESA' && this.user()?.empresaRole !== 'VISUALIZADOR',
  );
  readonly shellPath = computed(() => {
    const r = this.role();
    if (r === 'EMPRESA') return '/empresa';
    if (r === 'PROFISSIONAL') return '/profissional';
    return '/welcome';
  });

  setSession(token: string, user: MeResponse): void {
    this.accessToken.set(token);
    this.user.set(user);
  }

  setAccessToken(token: string | null): void {
    this.accessToken.set(token);
  }

  clear(): void {
    this.accessToken.set(null);
    this.user.set(null);
  }

  /**
   * Phase 11 (MEDIA-02) — patch the avatar URL after a successful upload.
   *
   * No-op when no user is signed in. Mutates via spread so the previous
   * MeResponse object is left untouched (other readers may still hold it).
   */
  updateAvatarUrl(avatarUrl: string): void {
    const current = this.user();
    if (!current) return;
    this.user.set({ ...current, avatarUrl });
  }

  /**
   * Phase 11 (MEDIA-03) — patch the empresa logo URL after a successful upload.
   * Same immutability contract as updateAvatarUrl.
   */
  updateLogoUrl(logoUrl: string): void {
    const current = this.user();
    if (!current) return;
    this.user.set({ ...current, logoUrl });
  }

  /**
   * P-11 — patch the profissional's active funcoes in AuthStore.
   */
  updateFuncoes(funcoes: ('MONTADOR' | 'TECNICO' | 'CARREGADOR')[]): void {
    const current = this.user();
    if (!current) return;
    this.user.set({ ...current, funcoes });
  }

  /**
   * Atualiza papel da empresa ativa no AuthStore (RBAC: ADMINISTRADOR, COLABORADOR, VISUALIZADOR).
   */
  updateEmpresaRole(empresaRole: 'ADMINISTRADOR' | 'COLABORADOR' | 'VISUALIZADOR' | null): void {
    const current = this.user();
    if (!current) return;
    this.user.set({ ...current, empresaRole });
  }
}
